import React, { useState } from 'react';
import { 
  ChevronLeft, Car, CheckCircle, AlertCircle, Search, Loader,
  Camera, Zap, Shield, DollarSign, Info
} from 'lucide-react';
import { api } from '../services/api';

// Enhanced Vehicle Profile Wizard with Premium VIN Decode
const EnhancedVehicleProfileWizard = ({ onComplete, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [vinLoading, setVinLoading] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [errors, setErrors] = useState({});
  const [decodedData, setDecodedData] = useState(null);
  const [entryMode, setEntryMode] = useState('vin'); // 'vin' or 'manual'
  const [mileageUnit, setMileageUnit] = useState('miles'); // 'miles' or 'km'
  const [vehicleData, setVehicleData] = useState({
    vin: '',
    make: '',
    model: '',
    year: '',
    trim: '',
    engine: '',
    body_style: '',
    mileage: '',
    purchase_date: '',
    purchase_price: '',
    usage_type: 'daily',
    annual_miles: '',
    primary_driver: '',
    last_service_date: '',
    last_service_mileage: '',
    known_issues: '',
    recent_repairs: '',
    maintenance_preference: 'proactive',
    nickname: '',
    zip_code: ''
  });

  const handlePremiumVinDecode = async () => {
    if (!vehicleData.vin || vehicleData.vin.length !== 17) {
      setErrors({ vin: 'Please enter a valid 17-character VIN' });
      return;
    }

    setVinLoading(true);
    setErrors({});
// /vin/decode-premium/
    try {
      // Call premium VIN decode endpoint
      const response = await api.get(`/vin/decode-premium/${vehicleData.vin}`);

      const result = response.data;

      if (result.error === 'payment_required') {
        // Handle premium feature
        setErrors({ 
          vin: 'Premium VIN decode requires subscription. Basic decode will be used.' 
        });
        // Fall back to basic decode
        await handleBasicVinDecode();
        return;
      }

      if (result.vehicle) {
        setDecodedData(result);
        setVehicleData(prev => ({
          ...prev,
          make: result.vehicle.make || '',
          model: result.vehicle.model || '',
          year: result.vehicle.year?.toString() || '',
          trim: result.vehicle.trim || '',
          body_style: result.vehicle.body_type || '',
          engine: result.engine?.description || ''
        }));

        // Show success message with warning if model is missing
        if (!result.vehicle.model) {
          setErrors({ 
            success: 'VIN decoded successfully! Please manually enter the vehicle model.',
            model: 'Model not available from VIN decode. Please enter manually.'
          });
        } else {
          setErrors({ success: 'VIN decoded successfully! All vehicle details populated.' });
        }
      }
    } catch (error) {
      console.error('Premium VIN decode error:', error);
      setErrors({ vin: 'Failed to decode VIN. Please try again.' });
    } finally {
      setVinLoading(false);
    }
  };

  const handleBasicVinDecode = async () => {
    // Fallback to basic decode
    try {
      const response = await api.post('/vehicle-profiles/vin/decode', {
        vin: vehicleData.vin
      });

      const result = response.data;
      if (result.success) {
        setVehicleData(prev => ({
          ...prev,
          make: result.make || prev.make,
          model: result.model || prev.model,
          year: result.year?.toString() || prev.year,
          trim: result.trim || prev.trim
        }));
      }
    } catch (error) {
      console.error('Basic VIN decode error:', error);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (entryMode === 'vin') {
      // In VIN mode, VIN is required and must be 17 characters
      if (!vehicleData.vin || vehicleData.vin.length !== 17) {
        newErrors.vin = 'Please enter a valid 17-character VIN';
      }
    } else {
      // In manual mode, make, model, and year are required
      if (!vehicleData.make || vehicleData.make.trim() === '') {
        newErrors.make = 'Make is required';
      }
      if (!vehicleData.model || vehicleData.model.trim() === '') {
        newErrors.model = 'Model is required';
      }
      if (!vehicleData.year || vehicleData.year.trim() === '') {
        newErrors.year = 'Year is required';
      }
    }
    
    // Common required fields
    if (!vehicleData.mileage || vehicleData.mileage.trim() === '') {
      newErrors.mileage = 'Current mileage is required';
    }
    
    return newErrors;
  };

  const handleSubmit = async () => {
    setLoading(true);
    setErrors({});

    // Validate form based on entry mode
    console.log('Validating form with data:', vehicleData);
    const validationErrors = validateForm();
    console.log('Validation errors:', validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      console.log('Validation failed, not submitting');
      setErrors(validationErrors);
      setLoading(false);
      return;
    }
    
    console.log('Validation passed, submitting form');
    
    try {
      // Convert km to miles if user entered in km (1 km = 0.621371 miles)
      const kmToMiles = (km) => Math.round(km * 0.621371);
      
      let mileageInMiles = parseInt(vehicleData.mileage);
      let annualMilesInMiles = vehicleData.annual_miles && vehicleData.annual_miles.trim() 
        ? parseInt(vehicleData.annual_miles) 
        : null;
      
      if (mileageUnit === 'km') {
        mileageInMiles = kmToMiles(mileageInMiles);
        if (annualMilesInMiles) {
          annualMilesInMiles = kmToMiles(annualMilesInMiles);
        }
      }
      
      const profileData = {
        vin: vehicleData.vin && vehicleData.vin.trim() ? vehicleData.vin.trim() : null,
        make: vehicleData.make.trim(),
        model: vehicleData.model.trim(),
        year: parseInt(vehicleData.year),
        trim: vehicleData.trim && vehicleData.trim.trim() ? vehicleData.trim.trim() : null,
        engine: vehicleData.engine && vehicleData.engine.trim() ? vehicleData.engine.trim() : null,
        body_style: vehicleData.body_style && vehicleData.body_style.trim() ? vehicleData.body_style.trim() : null,
        mileage: mileageInMiles,
        purchase_date: vehicleData.purchase_date && vehicleData.purchase_date.trim() ? vehicleData.purchase_date.trim() : null,
        purchase_price: vehicleData.purchase_price && vehicleData.purchase_price.trim() ? parseFloat(vehicleData.purchase_price) : null,
        usage_type: vehicleData.usage_type && vehicleData.usage_type.trim() ? vehicleData.usage_type.trim() : null,
        annual_miles: annualMilesInMiles,
        primary_driver: vehicleData.primary_driver && vehicleData.primary_driver.trim() ? vehicleData.primary_driver.trim() : null,
        last_service_date: vehicleData.last_service_date && vehicleData.last_service_date.trim() ? vehicleData.last_service_date.trim() : null,
        last_service_mileage: vehicleData.last_service_mileage && vehicleData.last_service_mileage.trim() ? parseInt(vehicleData.last_service_mileage) : null,
        known_issues: vehicleData.known_issues && vehicleData.known_issues.trim() ? vehicleData.known_issues.trim() : null,
        recent_repairs: vehicleData.recent_repairs && vehicleData.recent_repairs.trim() ? vehicleData.recent_repairs.trim() : null,
        maintenance_preference: vehicleData.maintenance_preference && vehicleData.maintenance_preference.trim() ? vehicleData.maintenance_preference.trim() : null,
        nickname: vehicleData.nickname && vehicleData.nickname.trim() ? vehicleData.nickname.trim() : null
      };

      console.log('Sending profile data:', profileData);
      const response = await api.post('/vehicle-profiles/', profileData);

      if (response.status === 200 || response.status === 201) {
        const result = response.data;
        
        // If we have decoded data and the vehicle profile was created successfully,
        // store the premium data with the new vehicle profile ID
        if (decodedData && result.id) {
          try {
            await api.post(`/vehicle-profiles/${result.id}/store-premium-data`, decodedData);
            console.log('Premium data stored successfully');
          } catch (error) {
            console.warn('Error storing premium data:', error);
          }
        }
        
        onComplete(result);
      } else {
        throw new Error('Failed to create vehicle profile');
      }
    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 bg-surface-card min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-section-title font-semibold text-ink-soft">Add Vehicle Profile</h1>
        {onCancel && (
          <button onClick={onCancel} className="text-ink-muted hover:text-ink-soft">
            Cancel
          </button>
        )}
      </div>

      {/* Entry Mode Toggle */}
      <div className="mb-6">
        <div className="flex items-center justify-center space-x-4 mb-4">
          <span className="text-sm font-medium text-ink-soft">Entry Method:</span>
          <div className="flex bg-surface-muted rounded-lg p-1">
            <button
              type="button"
              onClick={() => setEntryMode('vin')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                entryMode === 'vin'
                  ? 'bg-accent text-white shadow-sm'
                  : 'text-ink-muted hover:text-ink-soft'
              }`}
            >
              <Search className="w-4 h-4 inline mr-2" />
              VIN Decoder
            </button>
            <button
              type="button"
              onClick={() => setEntryMode('manual')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                entryMode === 'manual'
                  ? 'bg-accent text-white shadow-sm'
                  : 'text-ink-muted hover:text-ink-soft'
              }`}
            >
              <Car className="w-4 h-4 inline mr-2" />
              Manual Entry
            </button>
          </div>
        </div>
        
        {entryMode === 'vin' && (
          <div className="bg-info-tint border border-info-border rounded-lg p-3">
            <div className="flex items-start">
              <Info className="w-5 h-5 text-accent mt-0.5 mr-2 flex-shrink-0" />
              <div className="text-sm text-info">
                <p className="font-medium">VIN Decoder</p>
                <p>Enter your 17-character VIN to automatically populate vehicle details. This method provides the most accurate information.</p>
              </div>
            </div>
          </div>
        )}
        
        {entryMode === 'manual' && (
          <div className="bg-success-tint border border-success-border rounded-lg p-3">
            <div className="flex items-start">
              <Info className="w-5 h-5 text-success mt-0.5 mr-2 flex-shrink-0" />
              <div className="text-sm text-success">
                <p className="font-medium">Manual Entry</p>
                <p>Enter vehicle details manually. This method is faster if you know your vehicle information.</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Form */}
      <div className="space-y-6">
        <div className="text-center">
          <Car className="w-16 h-16 text-accent mx-auto mb-4" />
          <h2 className="text-page-title text-ink-soft mb-2">Add Your Vehicle</h2>
          <p className="text-ink-muted">
            {entryMode === 'vin' 
              ? 'Enter your VIN for automatic details' 
              : 'Enter your vehicle details manually'
            }
          </p>
        </div>

        {/* VIN Section - Only show in VIN mode */}
        {entryMode === 'vin' && (
          <div className="bg-info-tint p-6 rounded-lg border border-info-border">
            <div className="flex items-start justify-between mb-4">
              <div>
                <label className="block text-sm font-medium text-ink-soft mb-1">
                  VIN (Vehicle Identification Number)
                </label>
                <p className="text-small text-ink-muted">
                  Premium decode extracts complete vehicle specifications
                </p>
              </div>
              <Shield className="w-5 h-5 text-accent" />
            </div>
            
            <div className="flex space-x-2">
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={vehicleData.vin}
                  onChange={(e) => setVehicleData({ ...vehicleData, vin: e.target.value.toUpperCase() })}
                  placeholder="17-character VIN"
                  maxLength={17}
                  className="w-full px-3 py-2 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
                />
                <div className="absolute right-3 top-2.5 text-small text-ink-muted">
                  {vehicleData.vin.length}/17
                </div>
              </div>
              
              <button
                type="button"
                onClick={() => setShowScanner(true)}
                className="px-3 py-2 border border-border-default rounded-lg hover:bg-surface-page"
                title="Scan VIN"
              >
                <Camera className="w-4 h-4" />
              </button>
              
              <button
                type="button"
                onClick={handlePremiumVinDecode}
                disabled={vinLoading || !vehicleData.vin || vehicleData.vin.length !== 17}
                className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {vinLoading ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Decode
                  </>
                )}
              </button>
            </div>
            
            {errors.vin && (
              <p className="text-danger text-sm mt-1 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.vin}
              </p>
            )}
            
            {errors.success && (
              <p className="text-success text-sm mt-1 flex items-center">
                <CheckCircle className="w-4 h-4 mr-1" />
                {errors.success}
              </p>
            )}
          </div>
        )}

        {/* Decoded Information Display */}
        {decodedData && (
          <div className="bg-success-tint border border-success-border rounded-lg p-4">
            <h3 className="font-medium text-success mb-2">Decoded Vehicle Information</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-success">Engine:</span>
                <p className="font-medium text-success">{decodedData.engine?.description}</p>
              </div>
              <div>
                <span className="text-success">Transmission:</span>
                <p className="font-medium text-success">{decodedData.transmission?.description}</p>
              </div>
              <div>
                <span className="text-success">Fuel Type:</span>
                <p className="font-medium text-success">{decodedData.engine?.fuel_type}</p>
              </div>
              <div>
                <span className="text-success">Body Type:</span>
                <p className="font-medium text-success">{decodedData.vehicle?.body_type}</p>
              </div>
            </div>
            {decodedData.is_electric && (
              <div className="mt-2 flex items-center text-success">
                <Zap className="w-4 h-4 mr-1" />
                Electric Vehicle Detected
              </div>
            )}
          </div>
        )}

        {/* Vehicle Details */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-ink-soft mb-1">
              Make <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              value={vehicleData.make}
              onChange={(e) => setVehicleData({ ...vehicleData, make: e.target.value })}
              placeholder="e.g., Honda"
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-accent ${
                entryMode === 'manual' && !vehicleData.make 
                  ? 'border-danger focus:border-danger' 
                  : 'border-border-strong focus:border-accent'
              }`}
              required={entryMode === 'manual'}
              disabled={entryMode === 'vin' && decodedData}
            />
            {entryMode === 'manual' && !vehicleData.make && (
              <p className="text-danger text-small mt-1">Make is required for manual entry</p>
            )}
            {errors.make && (
              <p className="text-danger text-small mt-1">{errors.make}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-soft mb-1">
              Model <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              value={vehicleData.model}
              onChange={(e) => setVehicleData({ ...vehicleData, model: e.target.value })}
              placeholder="e.g., Civic"
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-accent ${
                entryMode === 'manual' && !vehicleData.model 
                  ? 'border-danger focus:border-danger' 
                  : 'border-border-strong focus:border-accent'
              }`}
              required={entryMode === 'manual'}
              disabled={entryMode === 'vin' && decodedData && decodedData.vehicle?.model}
            />
            {entryMode === 'manual' && !vehicleData.model && (
              <p className="text-danger text-small mt-1">Model is required for manual entry</p>
            )}
            {errors.model && (
              <p className="text-danger text-small mt-1">{errors.model}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-ink-soft mb-1">
              Year <span className="text-danger">*</span>
            </label>
            <input
              type="number"
              value={vehicleData.year}
              onChange={(e) => setVehicleData({ ...vehicleData, year: e.target.value })}
              placeholder="e.g., 2020"
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-accent ${
                entryMode === 'manual' && !vehicleData.year 
                  ? 'border-danger focus:border-danger' 
                  : 'border-border-strong focus:border-accent'
              }`}
              required={entryMode === 'manual'}
              disabled={entryMode === 'vin' && decodedData}
            />
            {entryMode === 'manual' && !vehicleData.year && (
              <p className="text-danger text-small mt-1">Year is required for manual entry</p>
            )}
            {errors.year && (
              <p className="text-danger text-small mt-1">{errors.year}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-soft mb-1">Trim</label>
            <input
              type="text"
              value={vehicleData.trim}
              onChange={(e) => setVehicleData({ ...vehicleData, trim: e.target.value })}
              placeholder="e.g., LX, EX, Sport"
              className="w-full px-4 py-3 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent"
              disabled={entryMode === 'vin' && decodedData}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-ink-soft">
                Current Mileage <span className="text-danger">*</span>
              </label>
              <div className="flex bg-surface-muted rounded-md p-0.5">
                <button
                  type="button"
                  onClick={() => setMileageUnit('miles')}
                  className={`px-2 py-1 text-small font-medium rounded transition-colors ${
                    mileageUnit === 'miles'
                      ? 'bg-accent text-white'
                      : 'text-ink-muted hover:text-ink-soft'
                  }`}
                >
                  Miles
                </button>
                <button
                  type="button"
                  onClick={() => setMileageUnit('km')}
                  className={`px-2 py-1 text-small font-medium rounded transition-colors ${
                    mileageUnit === 'km'
                      ? 'bg-accent text-white'
                      : 'text-ink-muted hover:text-ink-soft'
                  }`}
                >
                  KM
                </button>
              </div>
            </div>
            <div className="relative">
              <input
                type="number"
                value={vehicleData.mileage}
                onChange={(e) => setVehicleData({ ...vehicleData, mileage: e.target.value })}
                placeholder={mileageUnit === 'miles' ? 'e.g., 45000' : 'e.g., 72000'}
                className={`w-full px-4 py-3 pr-16 border rounded-lg focus:ring-2 focus:ring-accent ${
                  errors.mileage ? 'border-danger focus:border-danger' : 'border-border-strong focus:border-accent'
                }`}
                required
              />
              <span className="absolute right-4 top-3.5 text-sm text-ink-muted">
                {mileageUnit === 'miles' ? 'mi' : 'km'}
              </span>
            </div>
            {errors.mileage && (
              <p className="text-danger text-small mt-1">{errors.mileage}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-soft mb-1">
              ZIP/Postal Code
              <span className="text-small text-ink-muted ml-1">(for local market data)</span>
            </label>
            <input
              type="text"
              value={vehicleData.zip_code}
              onChange={(e) => setVehicleData({ ...vehicleData, zip_code: e.target.value })}
              placeholder="e.g., 90210 or M5V 3A1"
              className="w-full px-4 py-3 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent"
            />
          </div>
        </div>

        {/* Additional Info */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-ink-soft mb-1">
              Vehicle Nickname (Optional)
            </label>
            <input
              type="text"
              value={vehicleData.nickname}
              onChange={(e) => setVehicleData({ ...vehicleData, nickname: e.target.value })}
              placeholder="e.g., My Civic, The Family Car"
              className="w-full px-4 py-3 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1">
                Purchase Price (Optional)
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-3.5 w-4 h-4 text-ink-muted" />
                <input
                  type="number"
                  value={vehicleData.purchase_price}
                  onChange={(e) => setVehicleData({ ...vehicleData, purchase_price: e.target.value })}
                  placeholder="25000"
                  className="w-full pl-10 pr-3 py-3 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1">
                Annual {mileageUnit === 'miles' ? 'Miles' : 'Kilometers'} (Optional)
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={vehicleData.annual_miles}
                  onChange={(e) => setVehicleData({ ...vehicleData, annual_miles: e.target.value })}
                  placeholder={mileageUnit === 'miles' ? 'e.g., 12000' : 'e.g., 19000'}
                  className="w-full px-4 py-3 pr-12 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent"
                />
                <span className="absolute right-4 top-3.5 text-sm text-ink-muted">
                  {mileageUnit === 'miles' ? 'mi' : 'km'}
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1">Usage Type</label>
              <select
                value={vehicleData.usage_type}
                onChange={(e) => setVehicleData({ ...vehicleData, usage_type: e.target.value })}
                className="w-full px-4 py-3 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent"
              >
                <option value="daily">Daily Commuter</option>
                <option value="family">Family Vehicle</option>
                <option value="weekend">Weekend/Recreation</option>
                <option value="work">Work Vehicle</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1">
                Maintenance Preference
              </label>
              <select
                value={vehicleData.maintenance_preference}
                onChange={(e) => setVehicleData({ ...vehicleData, maintenance_preference: e.target.value })}
                className="w-full px-4 py-3 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent"
              >
                <option value="proactive">Proactive - Stay ahead</option>
                <option value="reactive">Reactive - Fix when needed</option>
                <option value="budget">Budget - Minimize costs</option>
              </select>
            </div>
          </div>
        </div>

        {errors.submit && (
          <div className="bg-danger-tint border border-danger-border rounded-lg p-4">
            <p className="text-danger text-sm flex items-center">
              <AlertCircle className="w-4 h-4 mr-2" />
              {errors.submit}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-between items-center pt-6 border-t border-border-default">
          <button
            type="button"
            onClick={onCancel}
            className="flex items-center px-4 py-2 text-ink-muted hover:text-ink-soft"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </button>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex items-center px-6 py-2 bg-success text-white rounded-lg hover:bg-success/80 disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Create Profile
              </>
            )}
          </button>
        </div>
      </div>

      {/* VIN Scanner Modal (placeholder) */}
      {showScanner && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-surface-card rounded-card shadow-elevation-1 p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">VIN Scanner Coming Soon</h3>
            <p className="text-ink-muted mb-4">
              OCR VIN scanning will be available in the next update.
            </p>
            <button
              onClick={() => setShowScanner(false)}
              className="w-full px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-hover"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedVehicleProfileWizard;